import { useMemo } from "react";

interface AnalysisCategory {
	title: string;
	score?: number;
	content: string | string[];
	key: string;
	group?: string;
}

export const useAnalysisData = (analysis: any) => {
	// Parse the analysis data and organize it into categories
	return useMemo(() => {

		if (!analysis) return { categories: [], overallScore: null };

		const data = typeof analysis === 'string' ? JSON.parse(analysis) : analysis;

		// Define category groups
		const categoryGroups = {
			"Hard Skills & Experience": [
				"skills_possessed",
				"technologies_familiar",
				"years_experience_total",
				"years_experience_job",
				"education_level",
				"certifications_held",
				"skills",
				"experience",
				"education",
				"certifications",
				"technologies"
			],
			"Work Environment & Culture Preferences": [
				"hierarchy_preference",
				"decision_making_style",
				"meeting_tolerance",
				"communication_style",
				"collaboration_preference",
				"work_environment",
				"cultural_fit"
			],
			"Workload & Expectations": [
				"stress_tolerance",
				"work_life_balance_needs",
				"overtime_acceptance",
				"urgency_tolerance",
				"work_ethics"
			],
			"Innovation & Adaptability": [
				"innovation_flexibility",
				"change_resistance",
				"risk_tolerance",
				"technology_adaptation",
				"adaptability"
			],
			"Management & Oversight Preferences": [
				"trust_preference",
				"performance_evaluation_style",
				"management_style"
			],
			"Compensation & Career Growth": [
				"salary_expectation",
				"bonus_dependency",
				"promotion_expectation",
				"recognition_need",
				"compensation",
				"career_growth"
			],
			"Remote Work & Flexibility": [
				"remote_work_preference",
				"hybrid_work_flexibility",
				"availability_flexibility",
				"asynchronous_work_preference",
				"flexibility"
			],
			"Other": [] // Fallback for categories that don't fit in other groups
		};

		// Extract potential categories from the analysis data
		const categories: AnalysisCategory[] = [];

		// Try to extract common categories from the analysis
		const knownCategories = [
			{ key: 'skills', title: 'Skills Match' },
			{ key: 'experience', title: 'Experience Match' },
			{ key: 'education', title: 'Education Match' },
			{ key: 'certifications', title: 'Certifications Match' },
			{ key: 'technologies', title: 'Technologies Match' },
			{ key: 'cultural_fit', title: 'Cultural Fit' },
			{ key: 'work_environment', title: 'Work Environment Culture' },
			{ key: 'strengths', title: 'Key Strengths' },
			{ key: 'weaknesses', title: 'Development Areas' },
			{ key: 'summary', title: 'Summary' },
			{ key: 'overall', title: 'Overall Assessment' }
		];

		// Add all specific categories from our groups
		Object.entries(categoryGroups).forEach(([group, keys]) => {
			keys.forEach(key => {
				if (!knownCategories.some(cat => cat.key === key)) {
					const title = key
						.split('_')
						.map(word => word.charAt(0).toUpperCase() + word.slice(1))
						.join(' ');

					knownCategories.push({ key, title });
				}
			});
		});

		// Get overall score if it exists
		let overallScore = null;
		if (data.match_score) {
			overallScore = data.match_score;
		} else if (data.overall_score) {
			overallScore = data.overall_score;
		} else if (typeof data.score === 'number') {
			overallScore = data.score;
		}

		// Process known categories
		knownCategories.forEach(category => {
			if (data[category.key]) {
				let content = data[category.key];
				let score: number | undefined = undefined;

				// Handle both string and object formats
				if (typeof content === 'object' && !Array.isArray(content)) {
					if ('score' in content) {
						score = content.score;
					}

					// Prioritize using description or details fields
					if ('description' in content) {
						content = content.description;
					} else if ('details' in content) {
						content = content.details;
					} else if ('content' in content) {
						content = content.content;
					} else if ('value' in content) {
						content = content.value;
					} else {
						// Convert object to presentable string format, excluding certain keys
						const filteredKeys = ['code', 'raw', 'extracted_value'];
						const contentStr = Object.entries(content)
							.filter(([key]) => !filteredKeys.includes(key.toLowerCase()))
							.map(([key, value]) => {
								// Don't include the label "explanation" but keep the content
								if (key.toLowerCase() === 'explanation') {
									return value;
								}
								return `${key.charAt(0).toUpperCase() + key.slice(1)}: ${value}`;
							})
							.join('\n\n');

						content = contentStr || "No details available";
					}
				}

				// Find the appropriate group for this category
				let group = "Other";
				for (const [groupName, keys] of Object.entries(categoryGroups)) {
					if (keys.includes(category.key)) {
						group = groupName;
						break;
					}
				}

				categories.push({
					title: category.title,
					score,
					content,
					key: category.key,
					group
				});
			}
		});

		// Process any remaining keys that might be categories
		Object.keys(data).forEach(key => {
			if (!knownCategories.some(c => c.key === key) &&
				key !== 'match_score' &&
				key !== 'overall_score' &&
				key !== 'score' &&
				key !== 'extracted_value') {
				let content = data[key];
				let score: number | undefined = undefined;

				// Handle both string and object formats
				if (typeof content === 'object' && !Array.isArray(content)) {
					if ('score' in content) {
						score = content.score;
					}

					// Prioritize using description or details fields
					if ('description' in content) {
						content = content.description;
					} else if ('details' in content) {
						content = content.details;
					} else if ('content' in content) {
						content = content.content;
					} else if ('value' in content) {
						content = content.value;
					} else {
						// Convert object to presentable string format, excluding certain keys
						const filteredKeys = ['code', 'raw', 'extracted_value'];
						const contentStr = Object.entries(content)
							.filter(([key]) => !filteredKeys.includes(key.toLowerCase()))
							.map(([key, value]) => {
								// Don't include the label "explanation" but keep the content
								if (key.toLowerCase() === 'explanation') {
									return value;
								}
								return `${key.charAt(0).toUpperCase() + key.slice(1)}: ${value}`;
							})
							.join('\n\n');

						content = contentStr || "No details available";
					}
				}

				// Format title for display (capitalize, replace underscores)
				const title = key
					.split('_')
					.map(word => word.charAt(0).toUpperCase() + word.slice(1))
					.join(' ');

				// Find the appropriate group for this category
				let group = "Other";
				for (const [groupName, keys] of Object.entries(categoryGroups)) {
					if (keys.includes(key)) {
						group = groupName;
						break;
					}
				}

				categories.push({
					title,
					score,
					content,
					key,
					group
				});
			}
		});

		// Sort categories by group
		categories.sort((a, b) => {
			const groupOrder = Object.keys(categoryGroups);
			const aGroupIndex = groupOrder.indexOf(a.group || "Other");
			const bGroupIndex = groupOrder.indexOf(b.group || "Other");
			return aGroupIndex - bGroupIndex;
		});

		return {
			categories,
			overallScore
		};
	}, [analysis]);
};
